import { useEffect, useRef } from 'react'

import {
  clearPendingManagerHubVisit,
  getPendingManagerHubVisit,
  PendingManagerHubVisitAction,
  resolvePendingManagerHubVisit,
} from '../pendingManagerHubVisit'
import { type ManagerHubVisibilityParams } from '../types'
import { createManagerHubPromotionSignalRequestData } from '../utils'

import type { PutUserPropertyRequest } from '../../../models/user/User'

export interface PendingManagerHubVisitLogEvent {
  message: string
  extras: Record<string, unknown>
  error?: unknown
}

export interface UseRecordPendingManagerHubVisitParams extends ManagerHubVisibilityParams {
  /** True once the app may record: web, signed in, and the user record loaded. */
  isReady: boolean
  lyraId?: string
  managerHubPromotionSignal: unknown
  /** Persists the promotion signal; must be referentially stable (memoized by the caller). */
  writePromotionSignal: (lyraId: string, requestData: PutUserPropertyRequest) => Promise<unknown>
  /** Optional diagnostics sink; must be referentially stable. */
  log?: (event: PendingManagerHubVisitLogEvent) => void
}

/**
 * Records a pending Manager Hub visit (one made while logged out) as the promotion signal once the visitor
 * is signed in. The pending visit is cleared only after a successful write or when a promotion window is
 * already active, so inputs that are still loading (flags, customer context) or a write cut short by a page
 * handoff leave it for the next authenticated evaluation. Apps supply selectors and the write action.
 */
export const useRecordPendingManagerHubVisit = ({
  isReady,
  lyraId,
  managerHubPromotionSignal,
  writePromotionSignal,
  log,
  isManagerHubEnabled,
  productType,
  isUserMinor,
  language,
  isEssentialsEnabled,
  disableSelfCareTopics,
}: UseRecordPendingManagerHubVisitParams): void => {
  const isWriteInFlight = useRef(false)

  useEffect(() => {
    if (!isReady || !lyraId || isWriteInFlight.current) {
      return
    }
    const pendingVisitAt = getPendingManagerHubVisit()
    if (pendingVisitAt === undefined) {
      return
    }

    const action = resolvePendingManagerHubVisit({
      isManagerHubEnabled,
      productType,
      isUserMinor,
      language,
      isEssentialsEnabled,
      disableSelfCareTopics,
      managerHubPromotionSignal,
    })
    if (action === PendingManagerHubVisitAction.WAIT) {
      return
    }
    if (action === PendingManagerHubVisitAction.DISCARD) {
      clearPendingManagerHubVisit()
      log?.({
        message: 'Pending Manager Hub visit discarded: promotion window already active',
        extras: { pendingVisitAt, managerHubPromotionSignal },
      })
      return
    }

    const recordedAt = Date.now()
    isWriteInFlight.current = true
    writePromotionSignal(lyraId, createManagerHubPromotionSignalRequestData(recordedAt))
      .then(() => {
        clearPendingManagerHubVisit()
        log?.({
          message: 'Pending Manager Hub visit recorded as promotion signal',
          extras: { pendingVisitAt, recordedAt },
        })
      })
      .catch((error: unknown) => {
        // The pending visit stays for a retry on the next authenticated evaluation.
        log?.({ message: 'Pending Manager Hub visit write failed', extras: { pendingVisitAt, recordedAt }, error })
      })
      .finally(() => {
        isWriteInFlight.current = false
      })
  }, [
    disableSelfCareTopics,
    isEssentialsEnabled,
    isManagerHubEnabled,
    isReady,
    isUserMinor,
    language,
    log,
    lyraId,
    managerHubPromotionSignal,
    productType,
    writePromotionSignal,
  ])
}
